'use client'

import { useState } from 'react'

type FormState = 'idle' | 'submitting' | 'success' | 'error'

const EMPTY = {
  name: '',
  organization: '',
  email: '',
  organization_type: 'Hospital or health system',
  geography: '',
  question: '',
}

export function FreeReadForm() {
  const [formState, setFormState] = useState<FormState>('idle')
  const [formData, setFormData] = useState(EMPTY)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormState('submitting')

    try {
      // Same submission path as /contact: Formspree via env var, with /api/contact fallback.
      const formspreeId = process.env.NEXT_PUBLIC_FORMSPREE_ID
      const endpoint = formspreeId
        ? `https://formspree.io/f/${formspreeId}`
        : '/api/contact'

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      })

      if (res.ok) {
        setFormState('success')
        setFormData(EMPTY)
      } else {
        setFormState('error')
      }
    } catch (err) {
      console.error('Free-read form submission error:', err)
      setFormState('error')
    }
  }

  if (formState === 'success') {
    return (
      <div className="form-status ok" role="status">
        Thanks &mdash; your request is in. We&rsquo;ll run one read and get back to you within one business day.
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <label htmlFor="name">Name</label>
        <input
          id="name"
          name="name"
          type="text"
          autoComplete="name"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor="org">Organization</label>
        <input
          id="org"
          name="organization"
          type="text"
          autoComplete="organization"
          required
          value={formData.organization}
          onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor="email">Work email</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor="type">What kind of organization?</label>
        <select
          id="type"
          name="organization_type"
          value={formData.organization_type}
          onChange={(e) => setFormData({ ...formData, organization_type: e.target.value })}
        >
          <option>Hospital or health system</option>
          <option>CDFI or community lender</option>
          <option>Foundation or funder</option>
          <option>Government or public agency</option>
          <option>Corporate marketing</option>
          <option>Other</option>
        </select>
      </div>
      <div>
        <label htmlFor="geo">Which geography should we read?</label>
        <input
          id="geo"
          name="geography"
          type="text"
          placeholder="Any city, county, or service area"
          required
          value={formData.geography}
          onChange={(e) => setFormData({ ...formData, geography: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor="q">What are you trying to figure out?</label>
        <textarea
          id="q"
          name="question"
          placeholder="The program, the population, or the decision in front of you."
          value={formData.question}
          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
        />
      </div>
      {formState === 'error' && (
        <div className="form-status err" role="alert">
          Something went wrong. Please try again, or email us directly.
        </div>
      )}
      <button className="btn" type="submit" disabled={formState === 'submitting'}>
        {formState === 'submitting' ? 'Sending…' : 'Request my free read'}
      </button>
    </form>
  )
}
